import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

export type TextPhrase = { phrase: string; from: number; to: number };

interface Segment {
	text: string;
	from: number;
	to: number;
}

interface WordInfo {
	text: string;
	start: number;
	end: number;
}

export class TextPhraseExtractor {
	// Matches any non-whitespace character
	private static readonly WORD_REGEX = /\S+/gu;
	private static readonly WORDS_BEFORE_CENTRAL = 4;
	private static readonly WORDS_AFTER_CENTRAL = 2;

	public static getTextPhrasesAroundCursor(state: EditorState): TextPhrase[] {
		const { selection } = state;
		if (!selection || selection.from !== selection.to) {
			return [];
		}

		const { $from } = selection;
		const parent = $from.parent;
		const parentStart = $from.start($from.depth);
		const parentOffset = $from.parentOffset;

		const segments = this.extractPlainTextSegments(parent);
		const segment = this.findSegmentForCursor(segments, parentOffset);
		if (!segment) {
			return [];
		}

		const offsetInSegment = parentOffset - segment.from;
		const words = this.extractWordsFromSegment(segment);
		if (!words.length) {
			return [];
		}

		if (!this.isCursorTouchingWord(words, offsetInSegment)) {
			return [];
		}

		const centralWordIndex = this.findCentralWordIndex(words, offsetInSegment);
		const { nearbyWords, centralWordPos } = this.extractNearbyWords(
			words,
			centralWordIndex,
			parentStart,
			segment.from,
		);
		return this.generateTextPhrases(nearbyWords, centralWordPos);
	}

	private static extractPlainTextSegments(parent: PMNode): Segment[] {
		const segments: Segment[] = [];
		let currentSegment: Segment | null = null;

		const flushSegment = () => {
			if (currentSegment) {
				segments.push(currentSegment);
				currentSegment = null;
			}
		};

		const isPlainText = (child: PMNode): boolean =>
			child.isText && !child.marks?.some((mark) => mark.type.name === 'link');

		parent.forEach((child, offset) => {
			if (isPlainText(child)) {
				const text = child.text || '';
				if (currentSegment && offset === currentSegment.to) {
					currentSegment.text += text;
					currentSegment.to += text.length;
				} else {
					flushSegment();
					currentSegment = { text, from: offset, to: offset + text.length };
				}
			} else {
				flushSegment();
			}
		});
		flushSegment();
		return segments;
	}

	private static findSegmentForCursor(segments: Segment[], parentOffset: number): Segment | null {
		return segments.find((seg) => parentOffset >= seg.from && parentOffset <= seg.to) || null;
	}

	private static isCursorTouchingWord(words: WordInfo[], relativeOffset: number): boolean {
		return words.some((word) => relativeOffset >= word.start && relativeOffset <= word.end);
	}

	private static extractWordsFromSegment(segment: Segment): WordInfo[] {
		const words: WordInfo[] = [];
		for (const match of segment.text.matchAll(this.WORD_REGEX)) {
			words.push({
				text: match[0],
				start: match.index,
				end: match.index + match[0].length,
			});
		}
		return words;
	}

	private static findCentralWordIndex(words: WordInfo[], cursorOffset: number): number {
		if (words.length === 0) {
			return 0;
		}
		const lastIndex = words.length - 1;
		if (cursorOffset > words[lastIndex].end) {
			return lastIndex;
		}
		let idx = words.findIndex((w) => w.start <= cursorOffset && w.end >= cursorOffset);
		if (idx === -1) {
			idx = words.findIndex((w) => w.end === cursorOffset);
		}
		return idx === -1 ? lastIndex : idx;
	}

	private static extractNearbyWords(
		words: WordInfo[],
		centralIndex: number,
		basePos: number,
		segmentFrom: number,
	): { nearbyWords: Array<{ word: string; from: number; to: number }>; centralWordPos: number } {
		const adjustWord = (w: WordInfo) => ({
			word: w.text,
			from: basePos + segmentFrom + w.start,
			to: basePos + segmentFrom + w.end,
		});

		const startIndex = Math.max(0, centralIndex - this.WORDS_BEFORE_CENTRAL);
		const leftWords = words.slice(startIndex, centralIndex + 1).map(adjustWord);
		const rightWords = words
			.slice(centralIndex + 1, centralIndex + 1 + this.WORDS_AFTER_CENTRAL)
			.map(adjustWord);

		return {
			nearbyWords: leftWords.concat(rightWords),
			centralWordPos: leftWords.length - 1,
		};
	}

	private static generateTextPhrases(
		nearbyWords: Array<{ word: string; from: number; to: number }>,
		centralIndex: number,
	): TextPhrase[] {
		const phrases: TextPhrase[] = [];
		for (let start = 0; start <= centralIndex; start++) {
			for (let end = centralIndex; end < nearbyWords.length; end++) {
				const phrase = nearbyWords
					.slice(start, end + 1)
					.map((w) => w.word)
					.join(' ');
				phrases.push({
					phrase,
					from: nearbyWords[start].from,
					to: nearbyWords[end].to,
				});
			}
		}
		return phrases;
	}
}

export class Cache<T> {
	private readonly store = new Map<string, T>();

	constructor(private readonly maxSize: number = 10000) {
		this.maxSize = maxSize;
	}

	get(key: string, compute: () => T): T {
		if (this.store.has(key)) {
			const value = this.store.get(key);
			if (value) {
				return value;
			}
		}
		const value = compute();
		if (value) {
			this.set(key, value);
		}
		return value;
	}

	private set(key: string, value: T): void {
		if (this.store.size >= this.maxSize) {
			const oldestKey = this.store.keys().next().value;
			if (oldestKey) {
				this.store.delete(oldestKey);
			}
		}
		this.store.set(key, value);
	}
}
